'use client'

import CustomHeader from "@/components/layout/header";
import React, {ReactNode, useEffect, useState} from "react";
import {useTheme} from "@mui/material";
import CustomSidebar from "@/components/layout/sidebar";
import {shallowEqual, useDispatch, useSelector} from "react-redux";
import {
    settingsSelectors,
    updateGlobalLoading,
    updateLayout, updateSidebarStatus,
    updateSiteImages,
    updateWebContext
} from "@/redux/slices/settings";
import BreadCrumb from "@/components/breadcrumb/breadcrumb";
import {usePathname, useRouter} from "next/navigation";
import LoadingDots from "@/components/shared/client/loadingDots";
import {useAuthenticateSessionMutation, useGetSiteImagesMutation} from "@/redux/rtk/login/login";
import {updateUserContext} from "@/redux/slices/users";
import Cookies from 'js-cookie';
import {BASE_PATH} from "@/components/constants";

const CustomLayout = ({children}: { children: ReactNode }) => {
    const router = useRouter();
    const [authenticateSession] = useAuthenticateSessionMutation();
    const [getSiteImages] = useGetSiteImagesMutation();
    const {layout, dir, globalLoading, siteImages, sidebarStatus} = useSelector((state) => {
        const layout = settingsSelectors.getLayout(state);
        const dir = settingsSelectors.getDirection(state);
        const globalLoading = settingsSelectors.getGlobalLoading(state);
        const siteImages = settingsSelectors.getSiteImages(state);
        const sidebarStatus = settingsSelectors.getSidebarStatus(state);
        return {layout, dir, globalLoading, siteImages, sidebarStatus};
    }, shallowEqual);
    const dispatch = useDispatch();
    const [showSides, setShowSides] = useState<boolean>(false);
    const [scrollbarWidth, setScrollbarWidth] = useState(0);
    const [sidePadding, setSidePadding] = useState<{
        paddingLeft: number,
        paddingRight: number
    } | null>(null);
    const theme = useTheme();
    const pathName = usePathname();
    const [sideParamHeights, setSideParamHeights] = useState<{ header: number; breadcrumb: number }>({
        header: 3.5,
        breadcrumb: 2.2
    });

    useEffect(() => {
        if(!siteImages) {
            getSiteImages(null).then((res: any) => {
                if(res?.data?.data?.siteSettings?.[0]?.settings) {
                    dispatch(updateSiteImages({
                        images: JSON.parse(res.data.data.siteSettings[0].settings),
                        dir: res.data.data.imageDetails.pic
                    }));
                }
            })
        }
    }, [siteImages])

    useEffect(() => {
        const handleClick = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            const link = target.closest("a");
            if (link?.pathname !== window?.location?.pathname) {
                if (link && link.href.startsWith(window.location.origin)) {
                    dispatch(updateGlobalLoading(true))
                }
            }
        };
        document.addEventListener("click", handleClick);
        return () => {
            document.removeEventListener("click", handleClick);
        };
    }, []);

    useEffect(() => {
        const updateScrollbarWidth = () => {
            const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
            setScrollbarWidth(scrollbarWidth);
        };
        updateScrollbarWidth();
        window.addEventListener("resize", updateScrollbarWidth);
        return () => window.removeEventListener("resize", updateScrollbarWidth);
    }, []);

    useEffect(() => {
        dispatch(updateGlobalLoading(true))
        if (!window.location.pathname.startsWith(`${BASE_PATH}/error`)) {
            setShowSides(true);
            authenticateSession({}).then((response: any) => {
                if (response?.error?.message && response?.error?.message !== "Aborted") {
                    Cookies.set(`${process.env.NEXT_PUBLIC_COOKIE_PREFIX}_ERROR`, JSON.stringify({
                        code: 500,
                        message: response?.error?.message,
                        localesData: response?.error?.localesData ?? null
                    }), {expires: 365});
                    router.push(`/error`)
                }
                if (response?.data?.data?.code === 200) {
                    const {userContext, webContext} = response.data.data;
                    if (userContext) {
                        dispatch(updateUserContext(userContext));
                    }
                    if (webContext) {
                        dispatch(updateWebContext(webContext));
                        if (webContext.settings && Array.isArray(webContext.settings)) {
                            dispatch(updateLayout({
                                isHeader: webContext.settings.includes("showHeader"),
                                isSideBar: webContext.settings.includes("showSidebar"),
                                isFooter: webContext.settings.includes("showFooter"),
                                isBreadcrumb: webContext.settings.includes("showBreadcrumb")
                            }))
                        }
                    }
                }
            }).finally(() => {
                setTimeout(() => {
                    dispatch(updateGlobalLoading(false));
                    if(sidebarStatus) dispatch(updateSidebarStatus(false))
                }, 300)
            })
        } else {
            setShowSides(false)
            setTimeout(() => {
                dispatch(updateGlobalLoading(false))
            }, 300)
        }
    }, [pathName]);

    const adjustPadding = (padding: number) => {
        setSidePadding({
            paddingLeft: (dir === "ltr" && showSides) ? padding : 0,
            paddingRight: (dir === "rtl" && showSides) ? padding : 0
        })
    }

    return <>
        {globalLoading && <LoadingDots/>}
        {(layout?.isSideBar && showSides) && <CustomSidebar dir={dir} adjustPadding={adjustPadding}/>}
        <div className="df we1" style={{overflowX: "hidden"}}>
            <div className="main-section df fdc" style={{maxWidth: `calc(100% - ${scrollbarWidth}px)`}}>
                {(layout?.isHeader && showSides) &&
                    <CustomHeader headerHeight={sideParamHeights.header} sidePadding={sidePadding}/>}
                {(layout?.isBreadcrumb && showSides) &&
                    <BreadCrumb sidePadding={sidePadding} heights={sideParamHeights}/>}
                {!globalLoading && <div
                    className="main-body df fdc"
                    style={{
                        boxSizing: "border-box",
                        background: theme.palette?.backgrounds?.main,
                        paddingLeft: `${!showSides ? 0 : sidePadding?.paddingLeft}px`,
                        paddingRight: `${!showSides ? 0 : sidePadding?.paddingRight}px`,
                        paddingTop: `${!showSides ? 0 : ((!layout.isHeader ? 0 : sideParamHeights.header) + (!layout.isBreadcrumb ? 0 : sideParamHeights.breadcrumb))}rem`,
                        maxHeight: "100vh"
                    }}>
                    {children}
                </div>}
            </div>
        </div>
    </>
}

export default CustomLayout;